import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { UsersService } from 'src/app/users/users.service';
import { SiteDashboardService } from '../../site-dashboard.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import { SiteTemperatureUnit, UtilizationSettingType } from '../site-utilization-settings/site-utilization-settings.interface';

@Component({
	selector: 'app-site-settings',
	templateUrl: './site-settings.component.html',
	styleUrls: ['./site-settings.component.css']
})
export class SiteSettingsComponent implements OnInit, OnDestroy {
	errorMassages = 'g.invalid_data';
	siteId: number;

	voltageLevels: any;
	isValidVoltageLevel = true;
	currentUser: any = {};

	TemperatureUnit = SiteTemperatureUnit;
	temperatureUnit: number = SiteTemperatureUnit.FAHRENHEIT;
	batteryUtilizationSettings: any;
	batteriesWorkingDays: number[] = [];

	subSite: Subscription = new Subscription();
	permissionSub: Subscription = new Subscription();

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private sideMenuService: SideMenuService,
		private usersService: UsersService,
		private sitesService: SitesService,
		private notificationMessage: NotificationMessageService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
		private translateService: TranslateService
	) { }

	ngOnInit(): void {
		this.subSite = this.sideMenuService.currentSite.subscribe((data: any) => {
			if (!data || !data.id)
				return;

			const siteId = Number(this.route.parent?.snapshot.params.siteId);
			const customerId = Number(this.route.parent?.snapshot.params.customerId);
			this.commonService.setPageTabTitle(`${data.customer_name} - ${data.name} - ${this.translateService.instant('nav.site_settings')}`);

			if (data.id != siteId || data.customer_id != customerId)
				return;

			this.siteId = siteId;
			this.currentUser = this.usersService.getCurrentUser();
			this.loadTemperatureUnit();
			this.permissionSub = this.usersService.getPermissions(this.siteId).subscribe((permissions: any) => {
				if(!this.usersService.hasAccessPermission(permissions, 'basic_setup', 'write'))
					return this.router.navigate(['/unauthorized']);
			});
			this.voltageLevels = data.voltage_levels ? data.voltage_levels : this.siteDashboardService.voltageLevels;
		});
	}

	setVoltageLevelColors() {
		this.isValidVoltageLevel = this.isVoltageLimitsValid();
		if (!this.isValidVoltageLevel)
			return;

		this.sitesService.setSiteEndVoltageLevels(this.siteId, this.voltageLevels).subscribe((res: any) => {
			this.notificationMessage.setMessage('globalSuccessMsg',{clearOnXTimeNavigate: 1});
		});
	}

	loadTemperatureUnit() {
		this.sitesService.getSiteUtilizationSettings(this.siteId).subscribe((res: any) => {
			const settings = res?.[0];
			if (!settings)
				return;

			this.batteryUtilizationSettings = settings.batteries_utilization_settings || {};
			this.batteriesWorkingDays = settings.batteries_utilization_working_days || [];
			this.temperatureUnit = this.batteryUtilizationSettings.temperature_unit ?? SiteTemperatureUnit.FAHRENHEIT;
		});
	}

	saveTemperatureUnit() {
		if (!this.batteryUtilizationSettings)
			return;

		this.batteryUtilizationSettings.temperature_unit = this.temperatureUnit;
		this.sitesService.saveSiteUtilizationSettings(this.siteId, UtilizationSettingType.Battery, this.batteryUtilizationSettings, this.batteriesWorkingDays).subscribe((res: any) => {
			this.notificationMessage.setMessage('globalSuccessMsg', { clearOnXTimeNavigate: 1 });
		});
	}

	isVoltageLimitsValid(): boolean {
		let hasNullValue = false;
		let hasNegativeValue = false;
		this.voltageLevels.forEach((level: any) => {
			// fields should not be empty
			if (level.min == null || level.max == null)
				hasNullValue = true;
			// Check if there are negative values
			if (level.min < 0 || level.max < 0)
				hasNegativeValue = true;
		});

		if (hasNullValue) {
			this.errorMassages = "g.empty_field";
			return false;
		}

		if (hasNegativeValue) {
			this.errorMassages = "g.negative_values";
			return false;
		}

		for (let i = 0; i < this.voltageLevels.length; i++) {
			const currentLevel = this.voltageLevels[i];
			const nextLevel = this.voltageLevels[i + 1];

			// Check if levels are overlapping
			if (nextLevel && currentLevel.max > nextLevel.min) {
					this.errorMassages = "g.ranges_overlap";
					return false;
			}

			// Check if there are gaps
			if (nextLevel && nextLevel.min != currentLevel.max) {
				this.errorMassages = "g.ranges_gaps";
				return false;
			}

			// Check if min is less than max
			if (currentLevel.max <= currentLevel.min) {
				this.errorMassages = "g.max_less_than_min";
				return false;
			}
		}

		return true;
	}

	ngOnDestroy() {
		this.subSite.unsubscribe();
		this.permissionSub.unsubscribe();
	}
}
